import { Dispatch, SetStateAction } from "react";

import RecordActionsMenu from "../../../../../components/Drawers/Records/RecordActionsMenu";
import FieldFormatter from "../../../../../components/Records/Formatters/FieldFormatter";
import RecordNameCell from "../../../../../components/Records/RecordNameCell";
import { RecordSummarySection } from "../../../../Records/RecordDetailLayout";
import DotsOptionsMenu from "../../../../../components/Menus/DotsOptionsMenu";
import { ReusableViewIDs } from "../../../../../types/PageInterfaces";
import useImage from "../../../../../api/api-hooks/images/useImage";
import { Module } from "../../../../../types/TenantInterfaces";
import { DetailView } from "../../SharedToolbar/SharedToolbar";
import { CardsLayoutI } from "../../usePageViewTable";

interface Props {
  isSelected: boolean;
  onSelect: (() => void) | undefined;
  record: Record<string, any>;
  currentModule: Module;
  setOpenRecordData: Dispatch<SetStateAction<DetailView>>;
  imageField: string | null;
  imageFieldPresentation: string | null;
  metadataField: string | null;
  metadataFieldType: string | null;
  setUpdateRecordID: Dispatch<SetStateAction<string | null>>;
  maxHeight?: number;
  viewID: string;
  cardLayout: CardsLayoutI & { id: string };
  frontFieldsSection: RecordSummarySection;
  cardImageField?: string;
  reusableViews?: ReusableViewIDs;
}

const FolderCardRecord = ({
  isSelected,
  onSelect,
  record,
  currentModule,
  setOpenRecordData,
  imageField,
  metadataField,
  metadataFieldType,
  setUpdateRecordID,
  viewID,
  cardImageField,
  reusableViews,
}: Props) => {
  const { fileURL } = useImage(
    cardImageField ? getRecordImageInfo(record, cardImageField) : undefined
  );

  return (
    <div
      className="tw:w-full tw:pb-3 tw:mt-6 tw:bg-superlight tw:relative tw:inline-flex tw:flex-col tw:gap-2 tw:rounded-b-sm tw:rounded-tr-sm tw:drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]"
      style={{
        maxWidth: "348px",
      }}
    >
      <div className="tw:absolute tw:top-0 tw:left-1/2 tw:transform tw:-translate-x-[6px] tw:-translate-y-full tw:w-8 tw:h-5 tw:bg-transparent tw:rounded-bl-3xl tw:shadow-[calc(-1rem)_0.75rem_0_superlight]"></div>
      <div className="tw:absolute tw:top-0 tw:left-0 tw:w-1/2 tw:h-5 tw:-translate-y-full tw:bg-superlight tw:rounded-tl-[0.5rem] tw:rounded-tr-[1.5rem]"></div>
      {/* Header */}
      <div className="tw:w-full tw:flex tw:justify-between tw:items-center tw:pt-2 tw:pr-2">
        <div className="tw:flex tw:w-full">
          {!currentModule?.Dynamic ? (
            <div
              className={`tw:cursor-pointer tw:pt-1 tw:px-3`}
              onClick={onSelect}
            >
              <input
                className="tw:cursor-pointer"
                type="checkbox"
                checked={isSelected}
                readOnly
              />
            </div>
          ) : (
            <div className="tw:pr-2"></div>
          )}
          <div
            className="tw:flex tw:w-full tw:mr-1"
            style={{ contain: "layout" }}
          >
            <div className="tw:w-full">
              <RecordNameCell
                record={record}
                onOpen={() => {
                  if (currentModule?.Dynamic) return;

                  setOpenRecordData({
                    recordID: record?._id || record?.id,
                    moduleAPIName: currentModule?.APIName,
                    tab: reusableViews?.RecordDetail
                      ? `Reuse-${reusableViews?.RecordDetail}-Summary`
                      : "Summary",
                    viewID: viewID,
                  });
                }}
                imageField={imageField}
                metadataField={metadataField}
                metadataFieldType={metadataFieldType}
                maxWidth={235}
                fontSize={13}
                // @ts-ignore
                tooltipPlacement={"autoVertical"}
                className="pt-1"
                commentsWidget
              />
            </div>
          </div>
        </div>
        <DotsOptionsMenu
          options={
            <RecordActionsMenu
              record={record}
              toggleUpdateRecordDrawer={() => setUpdateRecordID(record._id)}
              module={currentModule}
            />
          }
          dotsSize={20}
          placement="autoVerticalEnd"
        />
      </div>
      {/* Image */}
      <div
        className="tw:w-full tw:justify-center tw:items-center tw:overflow-hidden tw:px-2"
        style={{
          height: "120px",
        }}
      >
        {fileURL ? (
          <img
            className="tw:object-cover tw:w-full tw:h-full tw:rounded-sm"
            src={fileURL}
            alt=""
          />
        ) : (
          <div className="tw:flex tw:flex-col tw:justify-center tw:items-center tw:w-full tw:h-full tw:text-light tw:bg-empty-state">
            <i className="mdi mdi-image-off tw:text-xl" />
            <small className="tw:font-medium">Image Not Found</small>
          </div>
        )}
      </div>
      {/* Fields */}
      <div className="tw:w-full tw:flex tw:flex-col tw:justify-end tw:pt-2">
        <div className="tw:flex tw:px-3 tw:items-center tw:justify-between tw:w-full tw:text-[13px]">
          <p className="tw:text-[13px] tw:font-semibold tw:mb-0">Modified:</p>
          <FieldFormatter
            field={currentModule?.Fields.find(
              (f) => f.APIName === "LastUpdated"
            )}
            value={record.LastUpdated}
            record={record}
            recordID={record?._id || record?.id}
            moduleAPIName={currentModule?.APIName}
            table
          />
        </div>

        <div className="tw:flex tw:pl-3 tw:items-center tw:justify-between tw:w-full tw:text-[13px]">
          <p className="tw:text-[13px] tw:font-semibold tw:mb-0">Tags: </p>
          <FieldFormatter
            field={currentModule?.Fields.find((f) => f.APIName === "Tags")}
            value={record.Tags}
            record={record}
            recordID={record?._id || record?.id}
            moduleAPIName={currentModule?.APIName}
            table
          />
        </div>
      </div>
    </div>
  );
};

function getRecordImageInfo(record: any, cardImageField: string) {
  let fileData = record?.[cardImageField];

  if (!fileData) return undefined;

  if (Array.isArray(fileData)) fileData = fileData?.[0];

  if (!fileData) return undefined;

  if (fileData?.PublicURL) return fileData.PublicURL;
  if (fileData?.File) return fileData?.File;

  return undefined;
}

export default FolderCardRecord;
